import { DatePicker as DatePickerAnt, Row } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../constants';
import { IcMenuCalendar } from '../../icons';
import colors from '../../constants/colors';

const { RangePicker } = DatePickerAnt;

export type DateFilterType =
  | 'Day'
  | 'Weekly'
  | '2 Weekly'
  | 'Monthly'
  | 'Yearly';

interface FilterDatePickerProps {
  onChange?: (dates: {
    start: string;
    end: string;
    filterType: DateFilterType;
  }) => void;
  defaultStartDate?: string;
  defaultEndDate?: string;
  defaultFilter?: DateFilterType;
  support2Week?: boolean;
  supportYear?: boolean;
  
  // Style customization props
  className?: string;
  style?: React.CSSProperties;
  buttonGroupClassName?: string;
  buttonGroupStyle?: React.CSSProperties;
  datePickerClassName?: string;
  datePickerStyle?: React.CSSProperties;
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  activeButtonClassName?: string;
  activeButtonStyle?: React.CSSProperties;
  calendarIcon?: React.ReactNode;
}

const ButtonRadioGroup = styled(Row)`
  height: 56px;
  border-radius: 4px;
  background: ${Colors.neutral.bg[4]};
  display: flex;
  flex-wrap: nowrap;
  gap: 0;
`;

const ButtonRadio = styled.button<{ $active?: boolean; $activeStyle?: React.CSSProperties; $customStyle?: React.CSSProperties }>`
  padding: 12px 24px;
  border: none;
  background: ${(props) =>
    props.$active ? Colors.neutral.bg[6] : 'transparent'};
  color: ${(props) => (props.$active ? 'white' : Colors.neutral.text[5])};
  ${(props) => props.$active && 'border-radius: 4px;'}
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-family: Poppins;
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.875;
  letter-spacing: 0%;

  &:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  &:last-child {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  &:hover {
    background: ${(props) =>
      props.$active ? Colors.neutral.bg[6] : Colors.neutral.bg[5]};
  }
  
  ${props => props.$customStyle && Object.entries(props.$customStyle).map(([key, value]) => `${key}: ${value};`)}
  ${props => props.$active && props.$activeStyle && Object.entries(props.$activeStyle).map(([key, value]) => `${key}: ${value};`)}
`;

const WrapDateTime = styled(Row)<{ $isRange?: boolean; $customStyle?: React.CSSProperties }>`
  border-radius: 4px;
  background: white;
  min-width: ${(props) => (props.$isRange ? 'unset' : '240px')};
  height: 56px;
  position: relative;
  padding: 0 12px;
  
  ${props => props.$customStyle && Object.entries(props.$customStyle).map(([key, value]) => `${key}: ${value};`)}
`;

const DatePickerStyled = styled(DatePickerAnt)`
  background: none;
  border: none;
  padding: 0;
  flex: 1;

  &&& input {
    text-align: right;
    padding: 0 ;
    margin-bottom: -5px;
    line-height: 30px;
    letter-spacing: 0%;
    text-transform: uppercase;
    font-family: Poppins;
    font-weight: 600;
    font-size: 1.25rem;
    line-height: 1.875rem;
    letter-spacing: 0%;
    color: ${colors.neutral.text[4]};
  }

  &.ant-picker:hover,
  &.ant-picker-focused {
    border: none;
    box-shadow: none;
  }
`;

const RangePickerStyled = styled(RangePicker)`
  background: none;
  border: none;
  padding: 0;
  flex: 1;

  input {
    text-align: right;
    padding: 0 !important;
    margin-bottom: -5px;
    font-family: Poppins !important;
    font-weight: 600;
    font-size: 20px !important;
    line-height: 30px;
    letter-spacing: 0%;
    text-transform: uppercase;
    color: #505050 !important;
  }

  &.ant-picker:hover,
  &.ant-picker-focused {
    border: none;
    box-shadow: none;
  }

  .ant-picker-active-bar {
    display: none;
  }
`;

const Container = styled.div<{ $customStyle?: React.CSSProperties }>`
  display: flex;
  gap: 16px;
  align-items: center;
  
  ${props => props.$customStyle && Object.entries(props.$customStyle).map(([key, value]) => `${key}: ${value};`)}
`;

const formatDateWithRelative = (date: Dayjs) => {
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  if (date.isSame(today, 'day')) {
    return ` Today, ${date.format('MM-DD-YYYY')}`;
  }
  if (date.isSame(yesterday, 'day')) {
    return ` Yesterday, ${date.format('MM-DD-YYYY')}`;
  }
  return date.format('MM-DD-YYYY');
};

const FilterDatePicker: React.FC<FilterDatePickerProps> = ({
  onChange,
  defaultStartDate = dayjs().format('MM-DD-YYYY'),
  defaultEndDate = dayjs().format('MM-DD-YYYY'),
  defaultFilter = 'Day',
  support2Week = true,
  supportYear = true,
  
  // Style customization props
  className,
  style,
  buttonGroupClassName,
  buttonGroupStyle,
  datePickerClassName,
  datePickerStyle,
  buttonClassName,
  buttonStyle,
  activeButtonClassName,
  activeButtonStyle,
  calendarIcon = <IcMenuCalendar width={32} height={32} style={{marginLeft:5}}/>
}) => {
  const [filterType, setFilterType] = useState<DateFilterType>(defaultFilter);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    dayjs(defaultStartDate)
  );
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs(defaultStartDate),
    dayjs(defaultEndDate),
  ]);

  useEffect(() => {
    setFilterType(defaultFilter);
    if (defaultFilter === '2 Weekly') {
      const startDate = selectedDate.startOf('week');
      const endDate = startDate.add(1, 'week').endOf('week');
      updateDateRangeForTwoWeekly([startDate, endDate]);
    } else {
      updateDateRange(selectedDate, defaultFilter);
    }
  }, [defaultFilter]);

  const updateDateRange = (date: Dayjs, type: DateFilterType) => {
    let startDate = date;
    let endDate = date;

    switch (type) {
      case 'Weekly':
        startDate = date.startOf('week');
        endDate = date.endOf('week');
        break;
      case 'Monthly':
        startDate = date.startOf('month');
        endDate = date.endOf('month');
        break;
      case 'Yearly':
        startDate = date.startOf('year');
        endDate = date.endOf('year');
        break;
      case 'Day':
        break;
      default:
        return;
    }

    onChange?.({
      start: startDate.format('MM-DD-YYYY'),
      end: endDate.format('MM-DD-YYYY'),
      filterType: type,
    });
  };

  const updateDateRangeForTwoWeekly = (dates: [Dayjs, Dayjs]) => {
    onChange?.({
      start: dates[0].format('MM-DD-YYYY'),
      end: dates[1].format('MM-DD-YYYY'),
      filterType: '2 Weekly',
    });
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (!date) return;
    updateDateRange(date, filterType);
    setSelectedDate(date);
  };

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) return;
    const [start, end] = dates;
    setDateRange([start, end]);
    updateDateRangeForTwoWeekly([start, end]);
  };

  const handleFilterTypeChange = (type: DateFilterType) => {
    setFilterType(type);
    if (type === '2 Weekly') {
      const startDate = dayjs().startOf('week');
      const endDate = startDate.add(1, 'week').endOf('week');
      const newRange: [Dayjs, Dayjs] = [startDate, endDate];

      setDateRange(newRange);
      updateDateRangeForTwoWeekly(newRange);
    } else {
      updateDateRange(selectedDate, type);
    }
  };

  const picker = useMemo(() => {
    switch (filterType) {
      case 'Yearly':
        return 'year';
      case 'Monthly':
        return 'month';
      case 'Weekly':
        return 'week';
      case 'Day':
        return 'date';
      default:
        return 'date';
    }
  }, [filterType]);

  const format = useMemo(() => {
    switch (filterType) {
      case 'Yearly':
        return 'YYYY';
      case 'Monthly':
        return 'MMMM, YYYY';
      case 'Weekly':
      case '2 Weekly':
        return 'MM-DD-YYYY';
      case 'Day':
        return (date: Dayjs) => formatDateWithRelative(date);
      default:
        return 'MM-DD-YYYY';
    }
  }, [filterType]);

  return (
    <Container className={className} $customStyle={style}>
      <ButtonRadioGroup className={buttonGroupClassName} style={buttonGroupStyle}>
        {(
          [
            'Day',
            'Weekly',
            ...(support2Week ? ['2 Weekly'] : []),
            'Monthly',
            ...(supportYear ? ['Yearly'] : []),
          ] as DateFilterType[]
        ).map((type) => (
          <ButtonRadio
            key={type}
            $active={filterType === type}
            className={`${buttonClassName || ''} ${filterType === type ? activeButtonClassName || '' : ''}`}
            $customStyle={buttonStyle}
            $activeStyle={activeButtonStyle}
            onClick={() => handleFilterTypeChange(type)}
          >
            {type}
          </ButtonRadio>
        ))}
      </ButtonRadioGroup>
      <WrapDateTime 
        align='middle' 
        $isRange={filterType === '2 Weekly'} 
        className={datePickerClassName}
        $customStyle={datePickerStyle}
      >
        {filterType === '2 Weekly' ? (
          <RangePickerStyled
            value={dateRange}
            onChange={(dates) =>
              handleRangeChange(dates as [Dayjs | null, Dayjs | null] | null)
            }
            allowClear={false}
            inputReadOnly
            placement='bottomRight'
            format={format}
            suffixIcon={calendarIcon}
          />
        ) : (
          <DatePickerStyled
            size='large'
            value={selectedDate}
            onChange={(date) => handleDateChange(date as Dayjs | null)}
            allowClear={false}
            picker={picker}
            inputReadOnly
            placement='bottomRight'
            format={format}
            suffixIcon={calendarIcon}
          />
        )}
      </WrapDateTime>
    </Container>
  );
};

export default FilterDatePicker;